<template>
  <BaseListPage
      id="destinationListTable"
      ref="listPageComponent"
      :name="name"
      :no-condition="false"
      :base-query="baseQuery"
      :store-path="storePath"
      :response-path="responsePath"
      :save-key="saveKey"
      :table-set-list="tableSetList"
      :table-data-list="sortedList"
      :editable="editable"
      :row-class-name="rowClassName"
      :no-search="noSearch"
      :conditions="searchConditions"
      title="出荷先マスタ"
      title-class="masterDestination-header"
      icon="stock"
      @search="onSearch"
      @searched="onSearched">
    <span slot="headerAction"
        class="action_button">
      <el-dropdown
          v-show="showActionButtonComponent"
          @command="actionCommand">
        <el-button size="small">
          アクション
          <i class="el-icon-arrow-down el-icon--right" />
        </el-button>
        <el-dropdown-menu
            slot="dropdown"
            class="header-action-menu">
          <el-dropdown-item
              :disabled="$isFunctionDisabled(define.PermissionKeys.Stock.Destination.List.EDIT)"
              :command="define.DESTINATION_MODE.EDIT">
            編集
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
      <BaseButton v-show="mode === define.DESTINATION_MODE.EDIT"
          @click="cancel">
        キャンセル
      </BaseButton>
      <BaseButton v-show="mode === define.DESTINATION_MODE.EDIT"
          :disabled="disableSaveButton"
          @click="save">
        保存
      </BaseButton>
    </span>
    <template
        slot="tableOperation"
        slot-scope="{ row }">
      <BaseOperationTooltip
          v-if="showTooltip"
          :list="getTooltipList(row.row)"
          class="operation-button"
          placement="bottom-end" />
    </template>
    <template
        slot="prefixTable">
      <div class="prefixTableRow">
        <el-button v-show="mode === define.DESTINATION_MODE.EDIT"
            size="small"
            @click="addRow">
          行追加
        </el-button>
      </div>
    </template>
  </BaseListPage>
</template>

<style scoped>
.prefixTableRow .el-button ::v-deep span {
  margin: 0px;
}
#destinationListTable ::v-deep .el-table__row.hidRow {
  display: none;
}
</style>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'

import * as define from '@/define'
import * as query from '@/queries/stock'
import common from '@/utils/common'
import compare from '@/utils/compareUtils'
import * as config from '@/config'
import message from '@/utils/message'

import { v4 as uuid } from 'uuid'
import x from '@/components/mixins/mixin'
import confirmDiscardOnLeave from '@/components/mixins/confirmDiscardOnLeave'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseListPage from '@/components/organisms/BaseListPage'
import BaseButton from '@/components/atoms/BaseButton'
import BaseOperationTooltip from '@/components/molecules/BaseOperationTooltip'

const EDITABLE = Object.freeze({
  [define.DESTINATION_MODE.NORMAL]: Object.freeze({}),
  [define.DESTINATION_MODE.EDIT]: Object.freeze({
    code: true, name: true, postalCode: true, stateCode: true, city: true, address1: true, address2: true, phone: true, displayOrder: true
  })
})

export default {
  name: 'DestinationList',
  components: {
    BaseListPage,
    BaseOperationTooltip,
    BaseButton
  },
  mixins: [ x, confirmDiscardOnLeave ],
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      isChanged: false,
      originDestinationList: []
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      mode: (state) => state.Destination.mode,
      searchKeyword: (state) => state.Destination.searchKeyword,
      isAndSearch: (state) => state.Destination.isAndSearch,
      searchConditions: (state) => state.Destination.searchConditions,
      tableSetList: (state) => state.Destination.tableSetList,
      tdLists: (state) => state.Destination.tdLists
    }),
    ...mapGetters({
      getSortedList: 'sortedList'
    }),
    ...mapGetters('LocalStorage', {
      sortCondition: 'sortCondition',
      conditions: 'searchConditions'
    }),
    saveKey () {
      return 'destinationCondition'
    },
    name () {
      return 'destination'
    },
    storePath () {
      return 'Destination'
    },
    baseQuery () {
      return query.GRAPH_QL_QUERY_DESTINATION_LIST
    },
    sortedList () {
      return this.getSortedList(this.storePath, this.sortCondition(this.name))
    },
    responsePath () {
      return 'data.destinations.edges'
    },
    editable () {
      return EDITABLE[this.mode]
    },
    showActionButtonComponent () {
      return (this.showActionButton && (this.$isFunctionShown(define.PermissionKeys.Stock.Destination.List.EDIT)))
    },
    showTooltip () {
      return this.mode !== define.DESTINATION_MODE.NORMAL
    },
    showActionButton () {
      return this.mode === define.DESTINATION_MODE.NORMAL
    },
    disableSaveButton () {
      return !this.isChanged
    },
    noSearch () {
      return this.mode !== define.DESTINATION_MODE.NORMAL
    },
    linkKey () {
      return 'node.uuid'
    },
    _tableSetList () {
      return [
        {
          itemKey: 'code', type: 'String', label: '出荷先コード', path: 'node.code', elementType: ELEMENT_TYPE.INPUT,
          validateRule: `required|max:50|regex:^[a-zA-Z0-9!-/:-@¥[-\`{-~]*$`, validateAs: '出荷先コード', editableId: 'code'
        },
        {
          itemKey: 'name', type: 'String', label: '出荷先名称', path: 'node.name', elementType: ELEMENT_TYPE.INPUT,
          validateRule: 'required|max:256', validateAs: '出荷先名称', editableId: 'name'
        },
        {
          itemKey: 'postalCode', type: 'String', label: '郵便番号', path: 'node.postalCode', elementType: ELEMENT_TYPE.INPUT,
          validateRule: 'required|max:256', validateAs: '郵便番号', editableId: 'postalCode'
        },
        {
          itemKey: 'stateCode', type: 'String', label: '都道府県', path: 'node.stateCode', elementType: ELEMENT_TYPE.INPUT,
          validateRule: 'required|max:256', validateAs: '都道府県', editableId: 'stateCode'
        },
        {
          itemKey: 'city', type: 'String', label: '市区町村', path: 'node.city', elementType: ELEMENT_TYPE.INPUT,
          validateRule: 'required|max:256', validateAs: '市区町村', editableId: 'city'
        },
        {
          itemKey: 'address1', type: 'String', label: '住所1', path: 'node.address1', elementType: ELEMENT_TYPE.INPUT,
          validateRule: 'required|max:256', validateAs: '住所1', editableId: 'address1'
        },
        {
          itemKey: 'address2', type: 'String', label: '住所2', path: 'node.address2', elementType: ELEMENT_TYPE.INPUT,
          validateRule: 'max:256', validateAs: '住所2', editableId: 'address2'
        },
        {
          itemKey: 'phone', type: 'String', label: '電話番号', path: 'node.phone', elementType: ELEMENT_TYPE.INPUT,
          validateRule: 'max:256', validateAs: '電話番号', editableId: 'phone'
        },
        {
          itemKey: 'displayOrder', type: 'Number', label: '表示順', path: 'node.displayOrder', elementType: ELEMENT_TYPE.INPUT_NUMBER,
          validateRule: 'required|max:11', validateAs: '表示順', editableId: 'displayOrder'
        },
        {
          itemKey: 'operation', isAvailable: true, isActive: true, fixed: 'right',
          type: 'Slot', label: '', path: 'operation', width: '60px', columnKey: 'operation', class: 'right'
        }
      ]
    }
  },
  watch: {
    tdLists: {
      handler: function (val, oldVal) {
        this.isChanged = this.$_ConfirmDiscardOnLeave_isChanged()
      },
      deep: true
    },
    mode: {
      handler: function (val, oldVal) {
        if (oldVal != null) {
          if (val === define.DESTINATION_MODE.EDIT) {
            // 編集モードに切り替える場合
            this.originDestinationList = _.cloneDeep(this.tdLists)
            this.$_ConfirmDiscardOnLeave_watchStart(this.tdLists)
          } else {
            this.$_ConfirmDiscardOnLeave_watchEnd()
          }
        }
      },
      deep: true
    }
  },
  async created () {
    this.$loadingService.show()
    try {
      this.$store.dispatch({
        type: 'set',
        states: {
          'Destination/tableSetList': this._tableSetList
        }
      })
      this.updateStore('mode', define.DESTINATION_MODE.NORMAL)
    } catch (error) {
      this.$notify.error({ error: error })
      throw error
    } finally {
      this.$loadingService.close()
    }
  },
  mounted () {
  },
  destroyed () {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  methods: {
    onSearch (keyword, conditions) {
    },
    onSearched (result) {
      // 検索後、データを再取得、通常モードに戻る
      this.updateStore('mode', define.DESTINATION_MODE.NORMAL)
    },
    rowClassName ({ row, rowIndex }) {
      if (row.node.isActive === 0) {
        return 'hidRow'
      }
    },
    /**
     * Tooltipの項目を返却する.
     * @param {Object} row レコード
     * @return {Array}
     */
    getTooltipList (row) {
      return [
        {
          title: '削除',
          click: () => this.deleteDestination(row)
        }
      ]
    },
    onBeforeUnload (event) {
      if (this.isChanged) {
        // 変更有の場合、再読み込み確認ダイアログを表示する.
        event.returnValue = 'changed'
      }
    },

    /**
     * アクションボタン
     * @param {Number} mode モード
     */
    actionCommand: async function (mode) {
      switch (mode) {
        case define.DESTINATION_MODE.EDIT:
          this.editDestination()
          break
        default: {}
      }
    },

    /**
     * EDIT処理
     */
    async editDestination () {
      // 編集モードに切り替える
      this.updateStore('mode', define.DESTINATION_MODE.EDIT)
    },
    /**
     * 新規出荷先行を生成.
     */
    addRow () {
      this.tdLists.push({ node: { uuid: uuid(), type: 'new', isActive: '1', code: '', name: '', postalCode: '', stateCode: '', city: '', address1: '', address2: '', phone: '', displayOrder: '' } })
    },
    /**
     * 出荷先削除を行う
     * @param {Number} row 出荷先レコード
     */
    deleteDestination (row) {
      const uuid = _.get(row, this.linkKey)
      const index = this.$_mixin_findIndex(this.sortedList, { [this.linkKey]: uuid })

      // 追加行を削除する場合、そのまま削除する
      if (row.node.type === 'new') {
        this.tdLists.splice(index, 1)
      } else {
        this.$set(this.tdLists[index].node, 'isActive', 0)
      }
    },
    /**
     * 出荷先編集保存を行う
     */
    async save () {
      // チェック処理
      if (!await this.$validator.validate()) {
        return
      }

      // 保存を行う
      this.$confirm('保存しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(async () => {
        try {
          this.$loadingService.show()
          const diffList = this.compareList()
          // 出荷先編集パラメータの作成
          const data = this.buildRequestData(diffList)
          // 出荷先編集保存を行う
          await this.updateDestinationApi(data)
          this.isChanged = false
          // 編集保存後、通常モードに切り替える
          this.updateStore('mode', define.DESTINATION_MODE.NORMAL)
          // 保存後、リスト再検索を行う
          await this.getDetailData()
          this.$notify.success('保存しました。')
        } catch (error) {
          const data = _.get(error, 'response.data')
          if (data == null) {
            // システムエラー
            common.addError(error, { msg: message.MSG_502() })
          } else {
            // APIエラー
            common.addError(error, { msg: message.MSG_560() })
          }
          this.$notify.error({ error: error })
          throw error
        } finally {
          this.$loadingService.close()
        }
      }).catch(() => {
        this.$notify.info('キャンセルしました。')
      })
    },
    /**
     * 出荷先編集キャンセルを行う
     */
    async cancel () {
      const cancelEdit = () => {
        this.updateStore('mode', define.DESTINATION_MODE.NORMAL)
        this.isChanged = false
      }
      if (!this.isChanged) {
        cancelEdit()
        return
      }
      this.$confirm('出荷先変更を破棄しますか？',
        define.MESSAGE_TITLE,
        {
          confirmButtonText: 'はい',
          cancelButtonText: 'いいえ',
          type: 'warning'
        }
      ).then(() => {
        // キャンセル後、リスト再検索を行う
        this.getDetailData()
        cancelEdit()
      }).catch(() => {
      })
    },
    /**
     * 出荷先マスタ情報を取得する。
     */
    async getDetailData () {
      await this.$refs.listPageComponent.search()
    },
    /**
     * 一覧 から出荷先更新APIリクエストパラメータ用の差分を抽出する.
     */
    compareList () {
      const diffList = compare.diff(this.originDestinationList, this.tdLists)
      return diffList
    },
    /**
     * 差分を元に、リクエストパラメータを作成する。
     */
    buildRequestData () {
      const diffList = compare.diff(this.originDestinationList, this.tdLists)
      let requestDataItem = []
      for (const index in diffList) {
        if (this.originDestinationList[index]) {
          const diffItem = diffList[index].node
          if (this.tdLists[index].node.isActive === 0) {
            requestDataItem.push(
              {
                uuid: this.tdLists[index].node.uuid,
                isDelete: 1 // 削除フラグ（有効）
              }
            )
          } else {
            requestDataItem.push(
              {
                uuid: this.tdLists[index].node.uuid,
                ...diffItem
              }
            )
          }
        } else {
          requestDataItem.push(
            {
              code: this.tdLists[index].node.code,
              name: this.tdLists[index].node.name,
              postalCode: this.tdLists[index].node.postalCode,
              stateCode: this.tdLists[index].node.stateCode,
              city: this.tdLists[index].node.city,
              address1: this.tdLists[index].node.address1,
              address2: this.tdLists[index].node.address2,
              phone: this.tdLists[index].node.phone,
              displayOrder: this.tdLists[index].node.displayOrder
            })
        }
      }
      const requestData = {}
      if (requestDataItem.length) {
        requestData.destinations = requestDataItem
      }
      return requestData
    },
    /**
     * 出荷先更新APIを呼び出す
     */
    async updateDestinationApi (data) {
      const url = config.EC_STOCK_DESTINATION
      const method = 'post'
      const response = await common.apiAxios(url, { method, data })
      return response
    },
    /**
     * ストアの情報を更新する
     */
    updateStore (key, val) {
      const storeKey = `Destination/${key}`
      this.$store.commit({
        type: 'set',
        states: {
          [storeKey]: val
        }
      })
    }
  }
}
</script>
